/**
 * Color Palette Generator
 *
 * Generates Tailwind-style color palettes from a base hex color.
 * Similar to how Tailwind generates color scales from 50 (lightest) to 950 (darkest).
 */

export interface ColorPalette {
  100: string; // Lightest (input color)
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string; // Darkest
}

export interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Converts hex color to HSL
 */
export function hexToHsl(hex: string): HSL {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Converts HSL to hex color
 */
export function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generates a color palette where the provided color is the darkest entry
 *
 * @param darkestColor - Hex color (with or without #) representing the darkest shade (800 level)
 * @returns ColorPalette object with 8 levels from 100 (lightest near #FFFFFF) to 800 (darkest)
 */
export function generateColorPalette(darkestColor: string): ColorPalette {
  const lightestHex = '#FFFFFF';
  const normalizedDarkest = darkestColor.startsWith('#')
    ? darkestColor
    : `#${darkestColor}`;

  const lightestHsl = hexToHsl(lightestHex);
  const darkestHsl = hexToHsl(normalizedDarkest);

  // When the lightest tone has almost no saturation (e.g. white), reuse the
  // darkest hue so interpolated colors keep the same tint instead of drifting
  // toward red/yellow hues.
  const startHue = lightestHsl.s <= 5 ? darkestHsl.h : lightestHsl.h;
  const startSaturation =
    lightestHsl.s <= 5
      ? Math.min(20, Math.max(5, darkestHsl.s * 0.25))
      : lightestHsl.s;

  const interpolate = (start: number, end: number, t: number) =>
    start + (end - start) * t;

  const interpolateHue = (start: number, end: number, t: number) => {
    const delta = ((end - start + 540) % 360) - 180;
    let hue = start + delta * t;

    if (hue < 0) {
      hue += 360;
    }
    if (hue >= 360) {
      hue -= 360;
    }

    return hue;
  };

  const palette: ColorPalette = {
    100: '',
    200: '',
    300: '',
    400: '',
    500: '',
    600: '',
    700: '',
    800: normalizedDarkest,
  };

  const levels = [100, 200, 300, 400, 500, 600, 700, 800];
  const easingExponent = 0.35; // Increase weight toward the darkest color so lighter tones stay closer to it

  levels.forEach((level, index) => {
    if (level === 800) {
      return;
    }

    const baseRatio = (index + 1) / levels.length;
    const t = Math.pow(baseRatio, easingExponent);
    const h = interpolateHue(startHue, darkestHsl.h, t);
    const s = interpolate(startSaturation, darkestHsl.s, t);
    const l = interpolate(lightestHsl.l, darkestHsl.l, t);

    palette[level as keyof ColorPalette] = hslToHex(h, s, l);
  });

  return palette;
}

/**
 * Maps a numeric value to a color from the palette based on its position in a range
 *
 * @param value - Normalized value between 0 and 1
 * @param palette - Color palette to use
 * @returns Hex color string
 *
 * @example
 * ```typescript
 * const palette = generateColorPalette('#251081');
 * const color = mapValueToColor(0.75, palette); // Higher value gets darker color
 * ```
 */
export function mapValueToColor(value: number, palette: ColorPalette): string {
  if (value >= 0.9) return palette[800];
  if (value >= 0.7) return palette[700];
  if (value >= 0.5) return palette[500];
  if (value >= 0.4) return palette[400];
  if (value >= 0.3) return palette[300];
  if (value >= 0.2) return palette[200];
  return palette[100];
}

/**
 * Analyzes a dataset and returns min/max values for color mapping per column
 *
 * @param data - 2D array of string values (table data)
 * @param excludeFirstColumn - Whether to exclude the first column (typically labels)
 * @returns Array of objects with min and max numeric values for each column
 */
export function getDataRangePerColumn(
  data: string[][],
  excludeFirstColumn: boolean = true,
): Array<{ min: number; max: number }> {
  if (data.length === 0) return [];

  const startIndex = excludeFirstColumn ? 1 : 0;
  const numColumns = data[0].length;
  const columnRanges: Array<{ min: number; max: number }> = [];

  // Initialize ranges for each column
  for (let colIndex = startIndex; colIndex < numColumns; colIndex++) {
    let min = Infinity;
    let max = -Infinity;
    const sampleValues: number[] = [];

    // Analyze all rows for this column
    data.forEach((row) => {
      if (row[colIndex] !== undefined) {
        const value = parseFloat(row[colIndex]);
        if (!isNaN(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
          // Collect sample values for debugging
          if (sampleValues.length < 5) {
            sampleValues.push(value);
          }
        }
      }
    });

    // Handle case where no numeric values found in this column
    if (min === Infinity || max === -Infinity) {
      columnRanges.push({ min: 0, max: 1 });
    } else {
      columnRanges.push({ min, max });
    }
  }

  return columnRanges;
}

/**
 * Determines the appropriate text color (black or white) based on background color brightness
 *
 * @param backgroundColor - Hex color string (e.g., '#ff0000')
 * @returns 'white' for dark backgrounds, 'black' for light backgrounds
 *
 * @example
 * ```typescript
 * const textColor = getContrastTextColor('#251081'); // Returns 'white'
 * const textColor = getContrastTextColor('#ede9fe'); // Returns 'black'
 * ```
 */
export function getContrastTextColor(
  backgroundColor: string,
): 'white' | 'black' {
  // Remove # if present
  const hex = backgroundColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance using the formula from WCAG
  // https://www.w3.org/WAI/GL/wiki/Relative_luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Use white text for dark backgrounds (luminance < 0.5)
  // Use black text for light backgrounds (luminance >= 0.5)
  return luminance < 0.5 ? 'white' : 'black';
}

/**
 * Get background color and text color for numeric cells (columnIndex > 0)
 */
export function getBackgroundTextColor({
  impactValue,
  palette,
  columnIndex,
}: {
  impactValue: string;
  palette: ColorPalette | undefined;
  columnIndex: number;
}): { backgroundColor: string; color: string } {
  // Get background color and text color for numeric cells (columnIndex > 0)
  let backgroundColor = '';
  let textColor = '';

  if (columnIndex > 0 && palette) {
    const numericValue = Math.abs(parseFloat(impactValue));
    if (!isNaN(numericValue)) {
      // Use invertColors=true so higher values get lighter colors
      const color = mapValueToColor(numericValue, palette);
      backgroundColor = color;

      // Determine appropriate text color for readability
      textColor = getContrastTextColor(color);
    }
  }

  return { backgroundColor, color: textColor };
}
